import { useState } from 'react';
import { format, isValid } from 'date-fns';
import { Input } from '@/components/ui/Input/Input';
import { CheckCircleIcon, ErrorIcon } from '@/components/ui/Icons';
import './DateAutofillInput.css';

interface DateAutofillInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
}

export function DateAutofillInput({
  value,
  onChange,
  placeholder,
  disabled,
  fullWidth,
}: DateAutofillInputProps) {
  const [error, setError] = useState('');
  const [showSuccessIcon, setShowSuccessIcon] = useState(false);

  const handleManualInput = (input: string) => {
    const today = new Date();

    if (!input.trim()) {
      setError('');
      setShowSuccessIcon(false);
      onChange('');
      return;
    }

    const regex = /^(\d{1,2})([.\s]?(\d{1,2}))?([.\s]?(\d{2}|\d{4}))?$/;
    const match = input.match(regex);

    if (!match) {
      setError('Введите корректное значение');
      onChange('');
      setShowSuccessIcon(false);
      return;
    }

    const day = parseInt(match[1], 10);
    let month = parseInt(match[3] || String(today.getMonth() + 1), 10);
    const yearRaw = match[5];
    let year = yearRaw
      ? parseInt(yearRaw.length === 2 ? `20${yearRaw}` : yearRaw, 10)
      : today.getFullYear();

    if (match[1] && !match[3] && !match[5]) {
      if (day < today.getDate()) {
        month = today.getMonth() + 2;
        if (month > 12) {
          month = 1;
          year += 1;
        }
      } else {
        month = today.getMonth() + 1;
      }
    }

    if (match[1] && match[3] && !match[5]) {
      const inputDate = new Date(year, month - 1, day);
      if (inputDate < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
        year += 1;
      }
    }

    const finalDate = new Date(year, month - 1, day);

    if (!isValid(finalDate) || day > 31 || month > 12 || day < 1 || month < 1) {
      setError('Неверный формат');
      onChange('');
      setShowSuccessIcon(false);
      return;
    }

    const formatted = format(finalDate, 'dd.MM.yyyy');
    onChange(formatted);
    setError('');
    setShowSuccessIcon(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    const autoFormatted = inputValue
      .replace(/^(\d{2})(\d{1,2})$/, '$1.$2')
      .replace(/^(\d{2}[.]\d{2})(\d{1,4})$/, '$1.$2');

    if (/^[0-9.\s]*$/.test(inputValue)) {
      onChange(autoFormatted);
      setError('');
      setShowSuccessIcon(false);
    } else {
      setError('Только цифры, точки и пробелы');
      setShowSuccessIcon(false);
    }
  };

  return (
    <div className="date-autofill">
      <Input
        value={value}
        onChange={handleInputChange}
        onBlur={(e) => handleManualInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleManualInput((e.target as HTMLInputElement).value);
          }
        }}
        placeholder={error || placeholder}
        disabled={disabled}
        fullWidth={fullWidth}
        error={error}
        inputClassName={error ? 'date-autofill__input_error' : ''}
      />
      <div className={`date-autofill__icon ${showSuccessIcon && !error ? 'date-autofill__icon_success' : ''} ${error ? 'date-autofill__icon_error' : ''}`}>
        {showSuccessIcon && !error ? (
          <CheckCircleIcon size={20} aria-label="Дата валидна" />
        ) : error ? (
          <ErrorIcon size={20} aria-label={error} />
        ) : null}
      </div>
    </div>
  );
}
